import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { GeminiToolSummary, ReportingCoverage } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { GEMINI_METRIC_INFO } from '../metricInfo';
import { ChartCard } from './ChartCard';
import { CodexCacheEfficiencyChart } from './CodexCacheEfficiencyChart';
import { CodexDrillDownTable } from './CodexDrillDownTable';
import { EfficiencyGauge } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';
import { PricingBasisNote } from './PricingBasisNote';
import { ModelCostTable } from './ModelCostTable';

/**
 * The Gemini CLI body of the dashboard. Gemini's tokens arrive in the Codex
 * convention (input includes cached), so the Codex cache chart and drill-down
 * are reused as-is.
 */
export function GeminiDashboardBody({
  toolData,
  developersCount,
  coverage,
  formatCurrency,
  SectionHeading,
}: {
  toolData: GeminiToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const grouped = groupGeminiTimeSeries(toolData.timeSeries);
  const inputTokens = toolData.byModel.reduce((sum, m) => sum + m.inputTokens, 0);
  const outputTokens = toolData.byModel.reduce((sum, m) => sum + m.outputTokens, 0);
  const reasoningTokens = toolData.byModel.reduce((sum, m) => sum + m.reasoningTokens, 0);

  return (
    <>
      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <KpiCard
            label="Indicative token cost"
            value={formatCurrency(toolData.indicativeCost.totalCost, toolData.indicativeCost.currency)}
            meta={
              toolData.indicativeCost.unpricedModels.length > 0
                ? `Per-model pricing; ${toolData.indicativeCost.unpricedModels.length} model(s) at fallback rate`
                : 'Priced per model from Settings'
            }
            flag="Estimate"
            info={GEMINI_METRIC_INFO.indicativeCost}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Sessions"
            value={toolData.activity.sessions.toLocaleString()}
            meta="Distinct Gemini CLI sessions in range"
            info={GEMINI_METRIC_INFO.sessions}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active minutes"
            value={toolData.activity.activeMinutes.toLocaleString()}
            meta="Wall-clock minutes with activity"
            info={GEMINI_METRIC_INFO.activeMinutes}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={GEMINI_METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Tokens processed"
            value={(inputTokens + outputTokens).toLocaleString()}
            meta={`${inputTokens.toLocaleString()} in / ${outputTokens.toLocaleString()} out`}
            info={GEMINI_METRIC_INFO.tokensProcessed}
            accentClassName="bg-info"
          />
          <KpiCard
            label="Thinking tokens"
            value={reasoningTokens.toLocaleString()}
            meta={outputTokens > 0 ? `${Math.round((reasoningTokens / outputTokens) * 100)}% of output` : 'No output in range'}
            info={GEMINI_METRIC_INFO.reasoning}
            accentClassName="bg-info"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Cache hit ratio"
            value={toolData.cacheEfficiency.cacheHitRatio * 100}
            valueLabel={`${Math.round(toolData.cacheEfficiency.cacheHitRatio * 100)}%`}
            meta="Share of input tokens served from cache"
            info={GEMINI_METRIC_INFO.cacheEfficiency}
            size="sm"
          />
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={GEMINI_METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <div className="mt-3">
          <PricingBasisNote
            basis={{
              source: toolData.indicativeCost.source,
              reviewedAt: toolData.indicativeCost.pricesReviewedAt,
              stale: toolData.indicativeCost.pricesStale,
              fallbackCost: toolData.indicativeCost.byModel.filter((m) => m.pricedAs === null).reduce((sum, m) => sum + m.cost, 0),
            }}
            totalCost={toolData.indicativeCost.totalCost}
            unpricedModels={toolData.indicativeCost.unpricedModels}
          />
        </div>
      </section>

      <section>
        <ModelCostTable
          cost={toolData.indicativeCost}
          formatCurrency={formatCurrency}
          inputLabel="Billable input"
          cacheReadLabel="Cached input"
          showCacheWrite={false}
          info={GEMINI_METRIC_INFO.indicativeCost}
        />
      </section>

      <section>
        <SectionHeading title="Trends & breakdown" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <ChartCard
            title="Engagement trend"
            caption="Requests and sessions per day, corrected to max() per device"
            info={GEMINI_METRIC_INFO.engagementTrend}
            height="h-96"
            className="lg:col-span-7"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={grouped}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="date" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="requests" fill="#0058AB" name="Requests" />
                <Bar dataKey="sessions" fill="#06D6A0" name="Sessions" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Cache efficiency"
            caption="Billable (fresh) vs. cached input tokens"
            info={GEMINI_METRIC_INFO.cacheEfficiency}
            className="lg:col-span-5"
          >
            <CodexCacheEfficiencyChart cacheEfficiency={toolData.cacheEfficiency} />
          </ChartCard>

          <ChartCard
            title="Usage by model"
            caption="De-duplicated replies per model"
            info={GEMINI_METRIC_INFO.usageByModel}
            className="lg:col-span-12"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.byModel}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="model" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Bar dataKey="events" fill="#0058AB" name="Replies" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>

      <section>
        <CodexDrillDownTable breakdown={toolData.modelDeveloperBreakdown} info={GEMINI_METRIC_INFO.drillDown} />
      </section>
    </>
  );
}

function groupGeminiTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  const byDate = new Map<string, { date: string; requests: number; sessions: number }>();
  for (const point of timeSeries) {
    const row = byDate.get(point.date) ?? { date: point.date, requests: 0, sessions: 0 };
    if (point.metricName === 'gemini.requests') row.requests = point.value;
    if (point.metricName === 'gemini.sessions') row.sessions = point.value;
    byDate.set(point.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
